<template>
  <div class="neyos-tracker-statistics">
    <div v-if="loading" class="columns is-multiline">
      <div
        v-for="index in LAST_LIVES_COUNT"
        :key="`skeleton-${index}`"
        class="column is-half-tablet is-one-third-desktop is-2-fullhd"
      >
        <div class="box">
          <b-skeleton width="60%" />
          <b-skeleton width="40%" size="is-small" />
          <b-skeleton height="70px" />
          <b-skeleton width="80%" />
        </div>
      </div>
    </div>
    <div v-else-if="liveStatistics.length">
      <div class="columns is-multiline">
        <div
          v-for="(entry, index) in liveStatistics"
          :key="entry.live.id"
          class="column is-half-tablet is-one-third-desktop is-2-fullhd"
        >
          <neyos-tracker-statistics-live-block :entry="entry" :color="liveColor(index)" />
        </div>
        <div
          v-for="index in placeholderCount"
          :key="`placeholder-${index}`"
          class="column is-half-tablet is-one-third-desktop is-2-fullhd"
        >
          <div class="neyos-tracker-statistics__placeholder" />
        </div>
      </div>
      <div class="box">
        <p class="title is-6 mb-3">Batterie des derniers lives</p>
        <neyos-tracker-statistics-global-chart :entries="liveStatistics" />
      </div>
      <div class="box">
        <p class="title is-6 mb-3">Détail par étape</p>
        <neyos-tracker-statistics-table :entries="liveStatistics" />
      </div>
    </div>
    <p v-else class="has-text-grey has-text-centered">Aucun live pour ce tracker.</p>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import NeyosTrackerStatisticsGlobalChart from './NeyosTrackerStatisticsGlobalChart.vue'
import NeyosTrackerStatisticsLiveBlock from './NeyosTrackerStatisticsLiveBlock.vue'
import NeyosTrackerStatisticsTable from './NeyosTrackerStatisticsTable.vue'

const LAST_LIVES_COUNT = 6

const SECONDS_PER_HOUR = 60 * 60
const MIN_RATE_SECONDS = 30 * 60

const sum = (values) => values.reduce((total, value) => total + value, 0)

const aggregateBatteryStatistics = (statistics, stages) => {
  const stageStartedAtById = Object.fromEntries(stages.map((stage) => [stage.id, stage.startedAt]))

  const batteries = [...statistics]
    .sort(
      (left, right) => stageStartedAtById[left.liveStageId] - stageStartedAtById[right.liveStageId],
    )
    .map((statistics) => statistics.batteryStatistics)

  const motionConsumption = sum(batteries.map((battery) => battery.motionConsumption))
  const motionDischargeSeconds = sum(batteries.map((battery) => battery.motionDischargeSeconds))
  const idleConsumption = sum(batteries.map((battery) => battery.idleConsumption))
  const idleDischargeSeconds = sum(batteries.map((battery) => battery.idleDischargeSeconds))
  const motionSeconds = sum(batteries.map((battery) => battery.motionSeconds))
  const motionIntervalCount = sum(batteries.map((battery) => battery.motionIntervalCount))

  return {
    initialPercentage:
      batteries.map((battery) => battery.initialPercentage).find((value) => value !== null) ?? null,
    chargeCount: batteries.length ? sum(batteries.map((battery) => battery.chargeCount)) : null,
    averageMotionConsumptionPerHour:
      motionDischargeSeconds >= MIN_RATE_SECONDS && motionConsumption > 0
        ? motionConsumption / (motionDischargeSeconds / SECONDS_PER_HOUR)
        : null,
    averageIdleConsumptionPerHour:
      idleDischargeSeconds >= MIN_RATE_SECONDS && idleConsumption > 0
        ? idleConsumption / (idleDischargeSeconds / SECONDS_PER_HOUR)
        : null,
    averageMotionIntervalSeconds:
      motionIntervalCount > 0 ? motionSeconds / motionIntervalCount : null,
  }
}

const CURVE_COLORS = [
  '#2D62DD',
  '#2DAF32',
  '#A02DDD',
  '#FF741F',
  '#229CA0',
  '#E63737',
  '#CE3399',
  '#7B4336',
  '#585858',
]

export default {
  name: 'NeyosTrackerStatistics',

  components: {
    NeyosTrackerStatisticsGlobalChart,
    NeyosTrackerStatisticsLiveBlock,
    NeyosTrackerStatisticsTable,
  },

  props: {
    neyosTracker: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      liveStatistics: [],
    }
  },

  created() {
    this.LAST_LIVES_COUNT = LAST_LIVES_COUNT
    this.abortController = null

    this.load()
  },

  beforeDestroy() {
    this.abortController?.abort()
  },

  computed: {
    placeholderCount() {
      return LAST_LIVES_COUNT - this.liveStatistics.length
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    liveColor(index) {
      return CURVE_COLORS[index % CURVE_COLORS.length]
    },

    async load() {
      this.loading = true

      try {
        const [lives, liveVehicles] = await Promise.all([
          this.$services.liveService.getAllByNeyosTrackerId(this.neyosTracker.id),
          this.$services.liveVehicleService.getAllByNeyosTrackerId(this.neyosTracker.id),
        ])

        const vehicleNumbersByLiveId = Object.fromEntries(
          liveVehicles.map((vehicle) => [vehicle.liveId, vehicle.number]),
        )

        this.liveStatistics = [...lives]
          .sort((left, right) => right.startedAt - left.startedAt)
          .slice(0, LAST_LIVES_COUNT)
          .map((live) => ({
            live,
            vehicleNumber: vehicleNumbersByLiveId[live.id] || null,
            stages: [],
            statistics: [],
            aggregates: aggregateBatteryStatistics([], []),
            series: [],
            informationLoading: true,
            seriesLoading: true,
          }))
      } catch (error) {
        this.addToastMessage({ text: error.message, type: 'is-danger' })
        return
      } finally {
        this.loading = false
      }

      this.abortController = new AbortController()

      for (const entry of this.liveStatistics) {
        this.loadInformation(entry)
        this.loadSeries(entry)
      }
    },

    async loadInformation(entry) {
      try {
        const [stages, statistics] = await Promise.all([
          this.$services.liveStageService.getAll(entry.live.id),
          this.$services.liveNeyosTrackerStatisticsService.getAllByNeyosTrackerIdAndLiveId(
            this.neyosTracker.id,
            entry.live.id,
          ),
        ])

        entry.stages = stages
        entry.statistics = statistics
        entry.aggregates = aggregateBatteryStatistics(statistics, stages)
      } catch (error) {
        this.addToastMessage({ text: error.message, type: 'is-danger' })
      } finally {
        entry.informationLoading = false
      }
    },

    async loadSeries(entry) {
      try {
        const stream =
          this.$services.neyosTrackerChartSeriesService.getAllBatteryTimelinesByNeyosTrackerId(
            this.neyosTracker.id,
            entry.live.id,
            { signal: this.abortController.signal },
          )

        for await (const batch of stream) {
          entry.series = [
            ...entry.series,
            ...batch.map(({ id, points }) => ({
              id,
              points: points.map((point) => ({ x: point.x * 1000, y: point.y })),
            })),
          ]
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.addToastMessage({ text: error.message, type: 'is-danger' })
        }
      } finally {
        entry.seriesLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'variables';

.neyos-tracker-statistics__placeholder {
  height: 100%;
  border: 1px dashed $color-secondary-light;
  border-radius: 6px;
}
</style>
